import { create } from 'zustand';
import { Lead, LeadCreate, LeadUpdate } from '../types/lead';
import api from '../config/axios';
import { LEADS } from '../config/apiPaths';

interface LeadStore {
  leads: Lead[];
  currentLead: Lead | null;
  isLoading: boolean;
  error: string | null;

  fetchLeads: () => Promise<void>;
  fetchLeadById: (id: number) => Promise<void>;
  createLead: (lead: LeadCreate) => Promise<Lead>;
  updateLead: (id: number, lead: LeadUpdate) => Promise<Lead>;
  deleteLead: (id: number) => Promise<void>;

  getLeadById: (id: number) => Lead | undefined;
  clearError: () => void;
}

export const useLeadStore = create<LeadStore>((set, get) => ({
  leads: [],
  currentLead: null,
  isLoading: false,
  error: null,

  fetchLeads: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(LEADS.ALL);
      
      if (!Array.isArray(response.data)) {
        set({ leads: [], isLoading: false });
        return;
      }
      
      const leads = response.data.map((lead: any) => ({
        id: lead.id,
        created_at: lead.created_at,
        updated_at: lead.updated_at,
        from: lead.from,
        to: lead.to,
        subject: lead.subject,
        body: lead.body,
        direction: lead.direction,
        sent_date: lead.sent_date,
        read: lead.read,
        name: lead.name,
        phone: lead.phone,
        status_id: lead.status_id,
        proposal_id: lead.proposal_id,
        activity_id: lead.activity_id || lead.event_id // Handle both cases for backward compatibility
      }));
      
      set({ leads, isLoading: false });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch leads';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  fetchLeadById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(LEADS.BY_ID(id));
      set((state) => ({
        leads: [...state.leads.filter((l) => l.id !== id), response.data],
        currentLead: response.data,
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch lead';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  createLead: async (lead: LeadCreate): Promise<Lead> => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(LEADS.ALL, {
        from: lead.from,
        to: lead.to,
        subject: lead.subject,
        body: lead.body,
        direction: 'incoming',
       
        name: lead.name,
        phone: lead.phone || null
      });
      
      const newLead: Lead = {
        id: response.data.id,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
        from: response.data.from,
        to: response.data.to,
        subject: response.data.subject,
        body: response.data.body,
        direction: response.data.direction,
        sent_date: response.data.sent_date || new Date().toISOString(),
        read: false,
        name: response.data.name,
        phone: response.data.phone,
        status_id: response.data.status_id,
        proposal_id: response.data.proposal_id || null,
        activity_id: response.data.activity_id || null
      };
      
      set((state) => ({
        leads: [...state.leads, newLead],
        isLoading: false
      }));
      
      return newLead;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create lead';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  updateLead: async (id: number, updates: LeadUpdate) => {
    set({ isLoading: true, error: null });
    try {
      // Get the current lead to preserve required fields
      const currentLead = get().leads.find(lead => lead.id === id);
      if (!currentLead) {
        throw new Error('Lead not found');
      }
      
      // Merge updates with current lead data to ensure all required fields are included
      const updatedLead = {
        ...currentLead,
        ...updates,
        // Ensure sent_date is always included and properly formatted
        sent_date: updates.sent_date || currentLead.sent_date || new Date().toISOString(),
        // Ensure updated_at is always updated
        updated_at: new Date().toISOString()
      };
      
      const response = await api.put(LEADS.BY_ID(id), updatedLead);
      
      set((state) => ({
        leads: state.leads.map((l) =>
          l.id === id ? { ...l, ...response.data } : l
        ),
        currentLead:
          state.currentLead?.id === id ? { ...state.currentLead, ...response.data } : state.currentLead,
        isLoading: false,
      }));
      
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update lead';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  deleteLead: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(LEADS.BY_ID(id));
      set((state) => ({
        leads: state.leads.filter((l) => l.id !== id),
        currentLead:
          state.currentLead?.id === id ? null : state.currentLead,
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete lead';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  getLeadById: (id: number) => get().leads.find(l => l.id === id),
  clearError: () => set({ error: null }),
}));